/**
 * deliveryStatus — the one rung a bubble shows, derived from ledger facts
 * (2026-09-08, PR-2). Pure.
 *
 *   ○  pending            no fact yet (the send is seconds old)
 *   ⧗  queued-offline     browser offline at send; waiting for connection
 *   ✓  sent               the sender's own copy is on the server
 *   ✓✓ delivered          the thread carries it (group echo, or the turn was
 *                         accepted in a solo thread)
 *   ✓✓ read (accent)      a participant's receipt passed it (PR-3)
 *   !  failed             not sent — retry
 *
 * `null` = render nothing: not a user message, no shared id, or a message
 * this browser never saw sent (history). Precedence: failed > read >
 * delivered > sent > queued-offline > pending. A solo thread's `accepted` is
 * delivery: with no other participant, the server having the turn is the
 * whole story.
 */
import type { ChatMessage } from '../../components/GlobalChatSystem/types';
import type { DeliveryFacts } from './deliveryLedger';

export type DeliveryStatus = 'pending' | 'queued-offline' | 'sent' | 'delivered' | 'read' | 'failed';

export interface DeliveryStatusContext {
  /** True when the thread has other human participants (the shared store carries it). */
  isGroup: boolean;
  /**
   * PR-3: another participant's receipt covers this message (seenBy.readersOf
   * is non-empty). Lifts a delivered message to 'read'; like every rung it
   * needs session facts — a reload still paints nothing on history (the
   * SeenByMarks carries the durable read state).
   */
  readByOther?: boolean;
}

export function deriveDeliveryStatus(
  message: Pick<ChatMessage, 'isUser' | 'sharedMessageId'> | null | undefined,
  facts: DeliveryFacts | undefined,
  ctx: DeliveryStatusContext,
): DeliveryStatus | null {
  if (!message || message.isUser !== true) return null;
  if (typeof message.sharedMessageId !== 'string' || !message.sharedMessageId.trim()) return null;
  if (!facts) return null;
  if (facts.failedAt !== undefined) return 'failed';
  if (facts.readAt !== undefined || ctx.readByOther === true) return 'read';
  if (facts.deliveredAt !== undefined) return 'delivered';
  if (!ctx.isGroup && facts.acceptedAt !== undefined) return 'delivered';
  if (facts.ownAckedAt !== undefined) return 'sent';
  if (facts.queuedOfflineAt !== undefined) return 'queued-offline';
  return 'pending';
}

/** Chrome copy (D9: status, never content). */
export const DELIVERY_STATUS_COPY: Record<DeliveryStatus, { glyph: string; label: string; es: string }> = {
  pending: { glyph: '○', label: 'sending…', es: 'enviando…' },
  'queued-offline': { glyph: '⧗', label: "you're offline — this will send when you're back", es: 'sin conexión — se enviará cuando vuelvas' },
  sent: { glyph: '✓', label: 'sent', es: 'enviado' },
  delivered: { glyph: '✓✓', label: 'delivered', es: 'entregado' },
  read: { glyph: '✓✓', label: 'seen', es: 'visto' },
  failed: { glyph: '!', label: 'not sent · retry', es: 'no enviado · reintentar' },
};

/**
 * The reader's own human turns present in a shared-store snapshot — the
 * "delivered" fact for each. Pure so the thread store's listener stays a
 * one-liner and the rule is unit-tested.
 */
export function collectOwnDeliveredIds(
  overlay: ReadonlyArray<Pick<ChatMessage, 'id' | 'isUser' | 'senderId'>> | null | undefined,
  uid: string | null | undefined,
): string[] {
  const me = (uid ?? '').trim();
  if (!me || !Array.isArray(overlay)) return [];
  const out: string[] = [];
  for (const m of overlay) {
    if (!m || m.isUser !== true) continue;
    if ((typeof m.senderId === 'string' ? m.senderId.trim() : '') !== me) continue;
    const id = typeof m.id === 'string' ? m.id.trim() : '';
    if (id) out.push(id);
  }
  return out;
}
